<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'ticketAddPage',
})
</script>
<template>
  <div class="main detail-page">
    <a-card>
      <template #title>
        <span v-if="isEdit">{{ pageTitle }}</span>
      </template>
      <a-spin :spinning="state.loading.init">
        <div class="form-content">
          <a-form ref="formRef" :model="state.ruleForm" :rules="state.rules" :wrapper-col="{ style: 'flex: 1' }"
            class="ticket-add-form">
            <a-row :gutter="24" class="top-info-row">
              <a-col :span="24">
                <div class="top-info-content">
                  <div class="top-info-left">
                    <a-form-item label="展览封面" name="coverImage" class="cover-form-item">
                      <div class="cover-upload-wrap">
                        <a-upload v-if="!state.loading.init" v-model:file-list="fileList" :max-count="1" accept="image/*"
                          :before-upload="beforeUpload" list-type="picture-card" @change="handleUploadChange"
                          @preview="handlePreview" :show-upload-list="{ showPreviewIcon: true, showRemoveIcon: true }"
                          :disabled="uploadLoading">
                          <template #itemRender="{ originNode, file, actions: { preview, remove } }">
                            <template v-if="file.status === 'uploading'">
                              <VNodeRender :vnode="originNode" />
                            </template>
                            <div v-else class="ant-upload-list-item-container">
                              <div class="ant-upload-list-item ant-upload-list-item-done ant-upload-list-image">
                                <div class="ant-upload-list-item-thumbnail">
                                  <img v-if="isValidImageUrl(file.url || file.thumbUrl)" :src="file.url || file.thumbUrl"
                                    :alt="file.name" />
                                  <div v-else class="cover-error">
                                    <PictureOutlined />
                                  </div>
                                </div>
                                <span class="ant-upload-list-item-actions">
                                  <a v-if="isValidImageUrl(file.url || file.thumbUrl)" class="ant-upload-list-item-action"
                                    @click.stop="preview()" title="查看">
                                    <EyeOutlined />
                                  </a>
                                  <a v-if="isValidImageUrl(file.url || file.thumbUrl)" class="ant-upload-list-item-action"
                                    :href="file.url || file.thumbUrl" :download="file.name" title="下载">
                                    <DownloadOutlined />
                                  </a>
                                  <a class="ant-upload-list-item-action" @click.stop="remove()" title="删除">
                                    <DeleteOutlined />
                                  </a>
                                </span>
                              </div>
                            </div>
                          </template>
                          <div v-if="showUploadTrigger" class="upload-trigger-content">
                            <template v-if="uploadLoading">
                              <div class="manual-progress">
                                <div class="manual-progress-track">
                                  <div class="manual-progress-bar" :style="{ width: uploadProgress < 100 ? `${uploadProgress}%` : '100%' }"></div>
                                </div>
                                <div class="manual-progress-text">{{ uploadProgress < 100 ? `上传中 ${uploadProgress}%` : '处理中...' }}</div>
                              </div>
                            </template>
                            <template v-else>
                              <PlusOutlined />
                              <div class="ant-upload-text">上传封面</div>
                            </template>
                          </div>
                        </a-upload>
                      </div>

                      <a-image-preview-group ref="previewGroupRef"
                        :preview="{ visible: previewVisible, current: previewCurrent, wrapClassName: 'ticket-add-preview', onVisibleChange: (v: boolean) => previewVisible = v, onChange: (c: number) => previewCurrent = c }"
                        class="preview-group">
                        <a-image v-for="(url, index) in coverImageList" :key="index" :src="url" :alt="`图片${index + 1}`"
                          :style="{ display: 'none' }" />
                      </a-image-preview-group>
                    </a-form-item>

                    <a-form-item label="展览名称" name="exhibitionName" class="half-width-item">
                      <a-input v-model:value="state.ruleForm.exhibitionName" placeholder="请输入展览名称" :maxlength="30"
                        show-count allow-clear />
                    </a-form-item>

                    <a-form-item label="展览日期" name="dateRange" class="date-range-item half-width-item">
                      <ant-picker v-model:value="state.ruleForm.dateRange" value-format="YYYY-MM-DD"
                        :placeholder="['开始日期', '结束日期']" style="width: 100%;" :disabled="isEdit"
                        :disabled-date="disabledDate" :allow-empty="[true, true]" @calendar-change="onCalendarChange"
                        @change="onDateChange" @openChange="onOpenChange" />
                    </a-form-item>
                  </div>

                  <div class="top-info-right">
                    <a-form-item label="场次" required class="session-form-item"
                      :validate-status="sessionError ? 'error' : ''" :help="sessionError">
                      <div class="session-list">
                        <div v-for="(session, index) in state.ruleForm.sessions" :key="index" class="session-row"
                          :class="{ 'session-disabled': session.enabled === 0 || isEdit }">
                          <a-form-item-rest>
                            <a-select v-model:value="session.timeSlotCode" placeholder="请选择场次" class="session-select"
                              :disabled="isEdit" @change="(val: string) => onTimeSlotChange(index, val)">
                              <a-select-option v-for="slot in availableTimeSlots(index)" :key="slot.code"
                                :value="slot.code">{{
                                  slot.label }}</a-select-option>
                            </a-select>
                          </a-form-item-rest>
                          <div class="session-time">
                            <span class="label">时效</span>
                            <div class="session-time-picker">
                              <a-time-picker v-model:value="session.startTime" value-format="HH:mm" format="HH:mm"
                                placeholder="开始" class="session-time-start" :disabled="isEdit"
                                :disabled-hours="() => disabledStartHours(index)"
                                :disabled-minutes="(h: number) => disabledStartMinutes(index, h)" :show-now="false"
                                popup-class-name="ticket-time-picker-popup"
                                @openChange="(open: boolean) => onTimePickerOpen(open, index, 'start')"
                                @change="(t: any) => onStartTimeChange(index, t)" />
                              <span class="time-separator">-</span>
                              <a-time-picker v-model:value="session.endTime" value-format="HH:mm" format="HH:mm"
                                placeholder="结束" class="session-time-end" :disabled="isEdit"
                                :disabled-hours="() => disabledEndHours(index, session.startTime)"
                                :disabled-minutes="(h: number) => disabledEndMinutes(index, session.startTime, h)"
                                :show-now="false" popup-class-name="ticket-time-picker-popup"
                                @openChange="(open: boolean) => onEndPickerOpenChange(open, index)"
                                @change="(t: any) => onEndTimeChange(index, t)" />
                            </div>
                          </div>
                          <div class="session-price">
                            <span class="label">票价</span>
                            <div class="price-input-wrap">
                              <span class="price-symbol">￥</span>
                              <div class="session-price-input">
                                <a-input v-model:value="session.priceText" placeholder="请输入票价" :maxlength="11"
                                  :disabled="isEdit" allow-clear @input="(e: Event) => handlePriceInput(index, e)"
                                  @compositionend="(e: Event) => handlePriceInput(index, e)"
                                  @blur="() => handlePriceBlur(index)" />
                              </div>
                            </div>
                          </div>
                          <a-button type="primary" shape="circle" class="session-delete-btn"
                            :disabled="state.ruleForm.sessions.length <= 1 || isEdit" @click="removeSession(index)"
                            v-auth="'ticket.edit'">
                            <CloseOutlined />
                          </a-button>
                        </div>
                        <a-button type="link" @click="addSession"
                          :disabled="state.ruleForm.sessions.length >= 10 || isEdit" class="add-session-btn">
                          <PlusOutlined />新增场次
                        </a-button>
                      </div>
                    </a-form-item>
                  </div>
                </div>
              </a-col>
            </a-row>

            <div class="detail-section">
              <a-tabs v-model:activeKey="activeDetailKey" type="editable-card" @edit="onDetailTabsEdit"
                :hide-add="state.ruleForm.details.length >= 3" class="detail-tabs">
                <template #addIcon>
                  <PlusOutlined style="color: #0f813d;" />
                  <span style="margin-left: 4px; color: #0f813d; font-size: 16px;">新增详情</span>
                </template>
                <a-tab-pane v-for="(detail, index) in state.ruleForm.details" :key="`detail-${index}`"
                  :tab="`详情${index + 1}`" :closable="state.ruleForm.details.length > 1">
                  <a-form-item :label="`标题名称`" :name="['details', index, 'title']"
                    :rules="[{ required: true, message: '请输入标题', trigger: 'blur' }]" :wrapper-col="{ style: 'flex: 1' }"
                    class="detail-title-item half-width-item">
                    <a-input v-model:value="detail.title" placeholder="请输入详情标题" :maxlength="8" show-count allow-clear />
                  </a-form-item>
                  <a-form-item :name="['details', index, 'content']" class="editor-form-item no-label">
                    <WangEditor v-model:get-html="detail.content" height="300px" placeholder="请输入详情内容"
                      normalize-blank-lines />
                  </a-form-item>
                </a-tab-pane>
              </a-tabs>
              <div class="detail-footer">
                <a-button class="detail-close-btn" @click="onCancel">取 消</a-button>
                <a-button v-if="!isPublished" class="detail-confirm-btn" @click="onSaveDraft"
                  :loading="state.loading.saveDraft">保存草稿</a-button>
                <a-button class="detail-publish-btn" type="primary" @click="onPublish"
                  :loading="state.loading.publish">发布门票</a-button>
              </div>
            </div>
          </a-form>
        </div>
      </a-spin>
    </a-card>

    <ImageCropper v-model:visible="cropperVisible" :image-url="cropperImageUrl" :output-type="cropperOutputType"
      :crop-width="375" :crop-height="375" @confirm="handleCropConfirm" @cancel="handleCropCancel" />
  </div>
</template>

<script setup lang="ts" name="ticketAddPage">
import { reactive, ref, computed, onMounted, watch, h, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { message, Modal } from 'ant-design-vue'
import { PlusOutlined, ExclamationCircleOutlined, EyeOutlined, DownloadOutlined, DeleteOutlined, FileImageOutlined, CloseOutlined, PictureOutlined } from '@ant-design/icons-vue'
import { cloneDeep } from 'lodash-es'
import { useDictionary } from '@/store/system/dictionary'
import { useTicket, type TicketSession, type TicketDetail, type TimeSlotOption } from '@/store/ticket/ticket'
import WangEditor from '@/components/editor/index.vue'
import ImageCropper from '@/components/ImageCropper/index.vue'
import baseService from '@/utils/http/axios'
import storage from '@/utils/Storage'
import { ACCESS_TOKEN } from '@/store/mutation-types'
import dayjs from 'dayjs'

const route = useRoute()
const router = useRouter()
const ticketStore = useTicket()
const dictionaryStore = useDictionary()

const ticketId = computed(() => route.params.id as string | undefined)
const isEdit = computed(() => !!ticketId.value)
const pageTitle = computed(() => isEdit.value ? '编辑门票' : '新增门票')

const formRef = ref()
const fileList = ref<any[]>([])
const activeDetailKey = ref('detail-0')
const sessionError = ref('')
const timeSlotOptions = ref<TimeSlotOption[]>([])
const previewVisible = ref(false)
const previewGroupRef = ref()
const previewCurrent = ref(0)
const cropperVisible = ref(false)
const cropperImageUrl = ref('')
const cropperOutputType = ref<string>('png')
const uploadLoading = ref(false)
const uploadProgress = ref(0)
let pendingFile: File | null = null

const onTimePickerOpen = (open: boolean, index: number, type: 'start' | 'end') => {
  if (!open) return
  const session = state.ruleForm.sessions[index]
  const target = getDefaultOpenValue(index, type)
  if (!target) return
  const targetStr = target.format('HH:mm')
  // 当前值为空时，先把面板默认值真正赋给 v-model，确保点确定能选中合法时间
  if (type === 'start' && !session.startTime) {
    session.startTime = targetStr
  }
  if (type === 'end' && !session.endTime) {
    session.endTime = targetStr
  }
  const targetHour = target.hour()
  // 等面板渲染完，强制滚动到目标小时位置
  nextTick(() => {
    const panels = document.querySelectorAll('.ticket-time-picker-popup .ant-picker-time-panel-column')
    if (panels.length === 0) return
    const hourPanel = panels[0] as HTMLElement
    const rowHeight = 28
    hourPanel.scrollTop = targetHour * rowHeight
  })
}

const onEndPickerOpenChange = (open: boolean, index: number) => {
  if (open) {
    onTimePickerOpen(true, index, 'end')
  }
}

const isPublished = computed(() => {
  // 新增时默认未发布；编辑时 publishStatus 为 1 表示已发布
  if (!isEdit.value) return false
  return state.publishStatus === 1
})

const coverImageList = computed(() => {
  if (!state.ruleForm.coverImage) return []
  return isValidImageUrl(state.ruleForm.coverImage) ? [state.ruleForm.coverImage] : []
})

// 同时根据详情加载、封面字段和文件列表决定是否显示上传入口，避免删除过程的中间态抖动
const showUploadTrigger = computed(() => {
  return !state.loading.init && !state.ruleForm.coverImage && fileList.value.length < 1
})

const VNodeRender = (props: { vnode: any }) => props.vnode

const uploadHeaders = { Authorization: storage.get(ACCESS_TOKEN) || '' }

const uploadCover = async (file: File) => {
  const formData = new FormData()
  formData.append('file', file)
  uploadLoading.value = true
  uploadProgress.value = 0
  // 上传期间清空 fileList，让 trigger slot 显示进度条
  fileList.value = []
  try {
    const res: any = await baseService({
      url: '/console/file/uploadFile',
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...uploadHeaders
      },
      onUploadProgress: (progressEvent: any) => {
        const percent = progressEvent.total
          ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
          : 0
        uploadProgress.value = percent
      }
    })
    const url = typeof res.data === 'string' ? res.data : res.data?.url
    if (url) {
      fileList.value = [{
        uid: `-${Date.now()}`,
        name: file.name,
        status: 'done',
        url,
        thumbUrl: url
      }]
      state.ruleForm.coverImage = url
      // 清除表单封面字段的校验错误
      nextTick(() => {
        formRef.value?.clearValidate('coverImage')
        formRef.value?.validateFields(['coverImage']).catch(() => {})
      })
    } else {
      message.error('上传失败')
    }
  } catch (e) {
    message.error('上传失败')
  } finally {
    uploadLoading.value = false
    uploadProgress.value = 0
  }
}

const emptySession = (): TicketSession => ({
  timeSlotCode: undefined,
  startTime: '',
  endTime: '',
  price: undefined,
  priceText: '',
  enabled: 1,
  sortOrder: 0
})

const beforeUpload = (file: File) => {
  const isImage = file.type.startsWith('image/')
  const isLt10M = file.size / 1024 / 1024 < 10
  if (!isImage) {
    message.error('只能上传图片文件')
    return false
  }
  if (!isLt10M) {
    message.error('图片大小不能超过10MB')
    return false
  }
  pendingFile = file
  // 清空 fileList，避免 Ant Upload 把未裁剪文件加入列表
  fileList.value = []
  // 根据原图类型设置裁剪输出格式
  cropperOutputType.value = file.type === 'image/jpeg' || file.type === 'image/jpg' ? 'jpeg' : 'png'
  const reader = new FileReader()
  reader.onload = (e) => {
    cropperImageUrl.value = e.target?.result as string
    cropperVisible.value = true
  }
  reader.readAsDataURL(file)
  return false
}

const handleCropConfirm = async (file: File) => {
  if (pendingFile) {
    await uploadCover(file)
    pendingFile = null
  }
}

const handleCropCancel = () => {
  pendingFile = null
  cropperImageUrl.value = ''
  // 取消裁剪时：如果之前有已上传成功的封面，保留；否则清空
  const doneFiles = fileList.value.filter((f: any) => f.status === 'done' && f.url)
  if (doneFiles.length > 0) {
    fileList.value = doneFiles
  } else {
    fileList.value = []
    state.ruleForm.coverImage = ''
  }
}

const handleUploadChange = ({ fileList: list }: any) => {
  // 手动维护 fileList，删除时同步清空 coverImage 并触发校验
  if (list.length === 0) {
    fileList.value = []
    state.ruleForm.coverImage = ''
    nextTick(() => {
      formRef.value?.validateFields(['coverImage']).catch(() => {})
    })
  }
}

const parseTimeObj = (time?: string): string => {
  if (!time) return ''
  if (typeof time === 'string') return time
  return ''
}

const handlePriceInput = (index: number, e: Event) => {
  const target = e.target as HTMLInputElement
  let val = target.value
  // 过滤非数字和非小数点
  val = val.replace(/[^\d.]/g, '')
  // 最多保留一个小数点
  const firstDot = val.indexOf('.')
  if (firstDot !== -1) {
    val = val.substring(0, firstDot + 1) + val.substring(firstDot + 1).replace(/\./g, '')
  }
  // 整数部分最多8位，小数部分最多2位
  if (firstDot !== -1) {
    const intPart = val.substring(0, firstDot).slice(0, 8)
    const decPart = val.substring(firstDot + 1).slice(0, 2)
    val = intPart + '.' + decPart
  } else {
    val = val.slice(0, 8)
  }
  target.value = val
  const session = state.ruleForm.sessions[index]
  session.priceText = val
  session.price = val ? parseFloat(val) : undefined
}

const handlePriceBlur = (index: number) => {
  const session = state.ruleForm.sessions[index]
  const price = session.price
  if (price === undefined || price === null || Number.isNaN(price) || price <= 0) {
    session.priceText = ''
    session.price = undefined
  }
}

const compareTime = (a?: string, b?: string): number => {
  const ta = parseTimeObj(a)
  const tb = parseTimeObj(b)
  return ta.localeCompare(tb)
}

const isTimeBefore = (a?: string, b?: string): boolean => {
  return compareTime(a, b) < 0
}

const isTimeAfter = (a?: string, b?: string): boolean => {
  return compareTime(a, b) > 0
}

const parseHourMinute = (time?: string): [number, number] => {
  if (!time) return [-1, -1]
  const str = typeof time === 'string' ? time : dayjs(time).format('HH:mm')
  const parts = str.split(':')
  const h = parseInt(parts[0] || '', 10)
  const m = parseInt(parts[1] || '', 10)
  return [isNaN(h) ? -1 : h, isNaN(m) ? -1 : m]
}

const timeToMinutes = (time?: string): number => {
  const [h, m] = parseHourMinute(time)
  if (h < 0 || m < 0) return -1
  return h * 60 + m
}

const addOneMinute = (time?: string): string => {
  const [h, m] = parseHourMinute(time)
  if (h < 0 || m < 0) return ''
  let newH = h
  let newM = m + 1
  if (newM >= 60) {
    newM = 0
    newH = newH + 1
  }
  if (newH >= 24) return '23:59'
  return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`
}

// 前面所有已填行中，取每行 endTime || startTime 的最大值，再加 1 分钟作为当前行下限
const getLowerBound = (sessions: TicketSession[], index: number): string => {
  let max = -1
  let bound = ''
  for (let i = 0; i < index; i++) {
    const s = sessions[i]
    const t = s.endTime || s.startTime
    const mins = timeToMinutes(t)
    if (mins > max) {
      max = mins
      bound = t
    }
  }
  return bound ? addOneMinute(bound) : ''
}

// 后面所有已填行中，取每行 startTime || endTime 的最小值作为当前行上限
const getUpperBound = (sessions: TicketSession[], index: number): string => {
  let min = Number.MAX_SAFE_INTEGER
  let bound = ''
  for (let i = index + 1; i < sessions.length; i++) {
    const s = sessions[i]
    const t = s.startTime || s.endTime
    const mins = timeToMinutes(t)
    if (mins >= 0 && mins < min) {
      min = mins
      bound = t
    }
  }
  return bound
}

const disabledStartHours = (index: number) => {
  return []
}

const disabledStartMinutes = (index: number, selectedHour?: number) => {
  return []
}

const disabledEndHours = (index: number, startTime?: string) => {
  const disabled: number[] = []
  if (startTime) {
    const [startHour] = parseHourMinute(startTime)
    for (let i = 0; i < startHour; i++) disabled.push(i)
  }
  return disabled
}

const disabledEndMinutes = (index: number, startTime?: string, selectedHour?: number) => {
  if (selectedHour === undefined || selectedHour < 0) return []
  const disabled: number[] = []
  if (startTime) {
    const [startHour, startMinute] = parseHourMinute(startTime)
    if (selectedHour === startHour) {
      for (let i = 0; i <= startMinute; i++) disabled.push(i)
    }
  }
  return disabled
}

const isValidTime = (timeStr?: string): boolean => {
  if (!timeStr || typeof timeStr !== 'string') return false
  const [hourStr, minuteStr] = timeStr.split(':')
  const hour = parseInt(hourStr || '', 10)
  const minute = parseInt(minuteStr || '', 10)
  return !isNaN(hour) && !isNaN(minute) && hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59
}

const findFirstAvailableHour = (disabledHours: number[]): number => {
  for (let h = 0; h < 24; h++) {
    if (!disabledHours.includes(h)) return h
  }
  return 0
}

const findFirstAvailableMinute = (disabledMinutes: number[]): number => {
  for (let m = 0; m < 60; m++) {
    if (!disabledMinutes.includes(m)) return m
  }
  return 0
}

const isTimeDisabled = (type: 'start' | 'end', index: number, timeStr: string, startTime?: string): boolean => {
  const [h, m] = parseHourMinute(timeStr)
  if (h < 0 || m < 0) return true
  if (type === 'start') {
    return disabledStartHours(index).includes(h) || disabledStartMinutes(index, h).includes(m)
  }
  return disabledEndHours(index, startTime).includes(h) || disabledEndMinutes(index, startTime, h).includes(m)
}

const findFirstAvailableTime = (type: 'start' | 'end', index: number, startTime?: string): string | null => {
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m++) {
      const timeStr = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
      if (!isTimeDisabled(type, index, timeStr, startTime)) {
        return timeStr
      }
    }
  }
  return null
}

const getDefaultOpenValue = (index: number, type: 'start' | 'end'): dayjs.Dayjs | null => {
  const session = state.ruleForm.sessions[index]
  const now = dayjs()
  const currentHour = now.hour()
  const currentMinute = now.minute()
  const startTime = type === 'end' ? session.startTime : undefined

  // 已有值且合法 → 直接返回
  const currentValue = type === 'start' ? session.startTime : session.endTime
  if (currentValue && isValidTime(currentValue) && !isTimeDisabled(type, index, currentValue, startTime)) {
    const [h, m] = currentValue.split(':').map(Number)
    return dayjs().hour(h).minute(m).second(0)
  }

  // 结束时间优先从 startTime +1 分钟开始扫描
  if (type === 'end' && session.startTime && isValidTime(session.startTime)) {
    const [startHour, startMinute] = session.startTime.split(':').map(Number)
    let h = startHour
    let m = startMinute + 1
    if (m >= 60) {
      m = 0
      h = h + 1
    }
    for (; h < 24; h++) {
      for (; m < 60; m++) {
        const timeStr = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
        if (!isTimeDisabled(type, index, timeStr, startTime)) {
          return dayjs().hour(h).minute(m).second(0)
        }
      }
      m = 0
    }
  }

  // 开始时间或没有 startTime 的结束时间，从当前时间开始找
  let h = currentHour
  let m = currentMinute
  for (; h < 24; h++) {
    for (; m < 60; m++) {
      const timeStr = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
      if (!isTimeDisabled(type, index, timeStr, startTime)) {
        return dayjs().hour(h).minute(m).second(0)
      }
    }
    m = 0
  }

  // 从头再找一遍兜底
  const fallback = findFirstAvailableTime(type, index, startTime)
  if (!fallback) return null
  const [fh, fm] = fallback.split(':').map(Number)
  return dayjs().hour(fh).minute(fm).second(0)
}

const onStartTimeChange = (index: number, val: any) => {
  if (!val) return
  const time = typeof val === 'string' ? val : dayjs(val).format('HH:mm')
  state.ruleForm.sessions[index].startTime = time
}

const onEndTimeChange = (index: number, val: any) => {
  const session = state.ruleForm.sessions[index]
  const timeStr = val ? (typeof val === 'string' ? val : dayjs(val).format('HH:mm')) : ''
  session.endTime = timeStr
}

const emptyDetail = (): TicketDetail => ({
  title: '',
  content: ''
})

const emptyForm = {
  id: undefined as number | undefined,
  coverImage: '',
  exhibitionName: '',
  dateRange: [] as string[],
  startDate: '',
  endDate: '',
  sessions: [emptySession()] as TicketSession[],
  details: [emptyDetail()] as TicketDetail[]
}

const state = reactive({
  loading: {
    // 编辑页首次渲染即进入加载态，避免详情回显前闪现“上传封面”
    init: isEdit.value,
    saveDraft: false,
    publish: false
  },
  publishStatus: 0 as number | undefined,
  ruleForm: cloneDeep(emptyForm),
  rules: {
    coverImage: [{ required: true, message: '请上传展览封面', trigger: ['change', 'blur'] }],
    exhibitionName: [{ required: true, message: '请输入展览名称', trigger: 'blur' }],
    dateRange: [{ required: true, message: '请选择展览日期', trigger: 'change' }]
  }
})

const dates = ref<any[]>([])

const onCalendarChange = (val: any[]) => {
  const newStart = val?.[0]
  const newEnd = val?.[1]

  if (!newStart && !newEnd) {
    dates.value = []
    return
  }

  if (dates.value.length === 2 && dates.value[0] && dates.value[1]) {
    const oldStart = String(dates.value[0])
    const oldEnd = String(dates.value[1])

    if ((!newStart || newStart === oldStart) && (!newEnd || newEnd === oldEnd)) {
      dates.value = [oldStart, oldEnd]
      return
    }

    const isStartChanged = !!(newStart && newStart !== oldStart)
    const isEndChanged = !!(newEnd && newEnd !== oldEnd)

    if (isStartChanged && !isEndChanged) {
      handleStartChange(newStart, oldEnd)
      return
    }

    if (!isStartChanged && isEndChanged) {
      handleEndChange(oldStart, newEnd)
      return
    }

    if (isStartChanged && isEndChanged) {
      const diffToStart = Math.abs(dayjs(newStart).diff(oldStart, 'days'))
      const diffToEnd = Math.abs(dayjs(newEnd).diff(oldEnd, 'days'))
      if (diffToStart <= diffToEnd) {
        handleStartChange(newStart, oldEnd)
      } else {
        handleEndChange(oldStart, newEnd)
      }
    }
    return
  }

  if (newStart && !newEnd) {
    dates.value = [newStart, undefined]
  } else if (!newStart && newEnd) {
    dates.value = [undefined, newEnd]
  } else if (newStart && newEnd) {
    dates.value = [newStart, newEnd]
  }
}

const handleStartChange = (newStart: string, oldEnd: string) => {
  const startDate = dayjs(newStart)
  const endDate = dayjs(oldEnd)
  const diffDays = endDate.diff(startDate, 'days')
  dates.value = [newStart, undefined]
  if (diffDays >= 0 && diffDays <= 365) {
    state.ruleForm.dateRange = [newStart, oldEnd]
  } else {
    state.ruleForm.dateRange = [newStart, null]
  }
}

const handleEndChange = (oldStart: string, newEnd: string) => {
  const startDate = dayjs(oldStart)
  const endDate = dayjs(newEnd)
  const diffDays = endDate.diff(startDate, 'days')
  dates.value = [undefined, newEnd]
  if (diffDays >= 0 && diffDays <= 365) {
    state.ruleForm.dateRange = [oldStart, newEnd]
  } else {
    state.ruleForm.dateRange = [null, newEnd]
  }
}

const onOpenChange = (open: boolean) => {
  if (open) {
    const [start, end] = state.ruleForm.dateRange
    if (start && end) {
      dates.value = [start, end]
    } else if (start) {
      dates.value = [start, undefined]
    } else if (end) {
      dates.value = [undefined, end]
    } else {
      dates.value = []
    }
  } else {
    validateDateRange()
  }
}

const onDateChange = (val: (string | null)[]) => {
  if (val && val.length === 2) {
    const [startStr, endStr] = val
    if (startStr && endStr) {
      const start = dayjs(startStr)
      const end = dayjs(endStr)
      const diffDays = end.diff(start, 'days')
      if (diffDays < 0 || diffDays > 365) {
        if (dates.value[0] && !dates.value[1]) {
          state.ruleForm.dateRange = [startStr, null]
          dates.value = [startStr, undefined]
        } else if (!dates.value[0] && dates.value[1]) {
          state.ruleForm.dateRange = [null, endStr]
          dates.value = [undefined, endStr]
        } else {
          state.ruleForm.dateRange = []
          dates.value = []
        }
        return
      }
      state.ruleForm.dateRange = [startStr, endStr]
      if (dates.value[0] && !dates.value[1]) {
        dates.value = [startStr, undefined]
      } else if (!dates.value[0] && dates.value[1]) {
        dates.value = [undefined, endStr]
      } else {
        dates.value = [startStr, endStr]
      }
    } else if (startStr) {
      state.ruleForm.dateRange = [startStr, null]
      dates.value = [startStr, undefined]
    } else if (endStr) {
      state.ruleForm.dateRange = [null, endStr]
      dates.value = [undefined, endStr]
    } else {
      state.ruleForm.dateRange = []
      dates.value = []
    }
  }
}

const validateDateRange = () => {
  if (state.ruleForm.dateRange.length === 2 && state.ruleForm.dateRange[0] && state.ruleForm.dateRange[1]) {
    const start = dayjs(state.ruleForm.dateRange[0])
    const end = dayjs(state.ruleForm.dateRange[1])
    const diffDays = end.diff(start, 'days')
    if (diffDays < 0 || diffDays > 365) {
      const [startStr, endStr] = state.ruleForm.dateRange
      if (dates.value[0] && !dates.value[1]) {
        state.ruleForm.dateRange = [startStr, null]
        dates.value = [startStr, undefined]
      } else if (!dates.value[0] && dates.value[1]) {
        state.ruleForm.dateRange = [null, endStr]
        dates.value = [undefined, endStr]
      } else {
        state.ruleForm.dateRange = []
        dates.value = []
      }
      return true
    }
  }
  return true
}

const disabledDate = (current: any) => {
  if (!dates.value || dates.value.length === 0) return false
  const currentDate = dayjs(current)

  if (dates.value.length === 2 && dates.value[0] && dates.value[1]) {
    return false
  }

  if (dates.value[0] && !dates.value[1]) {
    const start = dayjs(dates.value[0])
    const diffDays = currentDate.diff(start, 'days')
    return diffDays < 0 || diffDays > 365
  }

  if (!dates.value[0] && dates.value[1]) {
    const end = dayjs(dates.value[1])
    const diffDays = end.diff(currentDate, 'days')
    return diffDays < 0 || diffDays > 365
  }

  return false
}

const availableTimeSlots = (currentIndex: number) => {
  const selectedCodes = state.ruleForm.sessions
    .map((s, i) => (i === currentIndex ? null : s.timeSlotCode))
    .filter(Boolean) as string[]
  return timeSlotOptions.value.filter((slot) => !selectedCodes.includes(slot.code))
}

const onTimeSlotChange = (index: number, code: string) => {
  const slot = timeSlotOptions.value.find((s) => s.code === code)
  const session = state.ruleForm.sessions[index]
  if (slot && session) {
    session.startTime = slot.startTime
    session.endTime = slot.endTime
    session.timeSlotCode = slot.code
  }
  sessionError.value = ''
}

const formatTimeSlotCode = (sessions: any[]) => {
  return sessions.map((s: any) => ({
    ...s,
    timeSlotCode: s.timeSlotCode || undefined
  }))
}

const addSession = () => {
  if (state.ruleForm.sessions.length >= 10) {
    message.warning('最多添加10个场次')
    return
  }
  state.ruleForm.sessions.push(emptySession())
  sessionError.value = ''
}

const removeSession = (index: number) => {
  if (state.ruleForm.sessions.length <= 1) {
    message.warning('至少保留一个场次')
    return
  }
  state.ruleForm.sessions.splice(index, 1)
  sessionError.value = ''
}

const onDetailTabsEdit = (targetKey: string | MouseEvent, action: string) => {
  if (action === 'add') {
    if (state.ruleForm.details.length >= 3) {
      message.warning('最多添加3个详情页')
      return
    }
    state.ruleForm.details.push(emptyDetail())
    activeDetailKey.value = `detail-${state.ruleForm.details.length - 1}`
  } else if (action === 'remove') {
    if (state.ruleForm.details.length <= 1) {
      message.warning('至少保留一个详情页')
      return
    }
    const index = Number((targetKey as string).replace('detail-', ''))
    Modal.confirm({
      transitionName: '',
      title: '提示',
      icon: () => h(ExclamationCircleOutlined),
      centered: true,
      content: '是否确认删除该详情页？',
      okText: '确认',
      cancelText: '取消',
      onOk: () => {
        const nextIndex = index === 0 ? 1 : index - 1
        state.ruleForm.details.splice(index, 1)
        activeDetailKey.value = `detail-${Math.min(nextIndex, state.ruleForm.details.length - 1)}`
      }
    })
  }
}

const handlePreview = (file: any) => {
  previewCurrent.value = 0
  previewVisible.value = true
}

const validateSessions = () => {
  sessionError.value = ''
  if (!state.ruleForm.sessions.length) {
    sessionError.value = '请至少添加一个场次'
    return false
  }
  for (const session of state.ruleForm.sessions) {
    if (!session.timeSlotCode) {
      sessionError.value = '请选择场次'
      return false
    }
    if (!session.startTime || !session.endTime) {
      sessionError.value = '请填写时效'
      return false
    }
    if (!isValidTime(session.startTime) || !isValidTime(session.endTime)) {
      sessionError.value = '时效格式不正确，请使用 HH:mm-HH:mm'
      return false
    }
    if (!isTimeAfter(session.endTime, session.startTime)) {
      sessionError.value = '结束时间必须晚于开始时间'
      return false
    }
    if (session.price === undefined || session.price === null || session.price <= 0) {
      sessionError.value = '请输入有效的票价'
      return false
    }
  }
  return true
}

const buildParams = () => {
  const [startDate, endDate] = state.ruleForm.dateRange
  const firstDetail = state.ruleForm.details[0] || emptyDetail()
  return {
    id: state.ruleForm.id || undefined,
    coverImage: state.ruleForm.coverImage,
    exhibitionName: state.ruleForm.exhibitionName,
    startDate,
    endDate,
    sessions: state.ruleForm.sessions.map((s, i) => ({
      timeSlotCode: s.timeSlotCode,
      startTime: parseTimeObj(s.startTime),
      endTime: parseTimeObj(s.endTime),
      price: s.price,
      enabled: s.enabled,
      sortOrder: i
    })),
    details: state.ruleForm.details.map((d) => ({
      title: d.title,
      content: d.content
    })),
    noticeTitle: firstDetail.title,
    noticeContent: firstDetail.content
  }
}

const handleValidateError = (error: any) => {
  if (!error?.errorFields?.length) return
  message.error('请完善表单信息')
  const firstField = error.errorFields[0].name
  if (!firstField || !Array.isArray(firstField)) return
  // 如果报错字段在 details 里，先切换到对应的详情 tab
  if (firstField[0] === 'details' && typeof firstField[1] === 'number') {
    activeDetailKey.value = `detail-${firstField[1]}`
  }
  nextTick(() => {
    formRef.value?.scrollToField(firstField)
  })
}

const onSaveDraft = () => {
  formRef.value.validate().then(async () => {
    if (!validateSessions()) return
    state.loading.saveDraft = true
    try {
      await ticketStore.saveTicketDraft(buildParams())
      message.success('保存草稿成功')
      router.push({ name: 'ticket' })
    } finally {
      state.loading.saveDraft = false
    }
  }).catch((error: any) => {
    handleValidateError(error)
  })
}

const onPublish = () => {
  formRef.value.validate().then(async () => {
    if (!validateSessions()) return
    Modal.confirm({
      transitionName: '',
      title: '提示',
      icon: () => h(ExclamationCircleOutlined),
      centered: true,
      content: '发布后门票信息将对用户可见，是否确认发布？',
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        state.loading.publish = true
        try {
          await ticketStore.publishTicket(buildParams())
          message.success('发布成功')
          router.push({ name: 'ticket' })
        } finally {
          state.loading.publish = false
        }
      }
    })
  }).catch((error: any) => {
    handleValidateError(error)
  })
}

const onCancel = () => {
  router.push({ name: 'ticket' })
}

const isValidImageUrl = (url: string) => {
  if (!url) return false
  return /^https?:\/\//i.test(url)
}

const loadDetail = async () => {
  if (!isEdit.value) return
  state.loading.init = true
  try {
    const data: any = await ticketStore.getTicketInfo(ticketId.value)
    if (!data) {
      message.error('门票信息不存在')
      router.push({ name: 'ticket' })
      return
    }
    state.ruleForm.id = data.id
    state.publishStatus = data.publishStatus
    state.ruleForm.coverImage = data.coverImage || ''
    state.ruleForm.exhibitionName = data.exhibitionName || ''
    state.ruleForm.startDate = data.startDate || ''
    state.ruleForm.endDate = data.endDate || ''
    state.ruleForm.dateRange = data.startDate && data.endDate ? [data.startDate, data.endDate] : []
    state.ruleForm.sessions = data.sessions?.length
      ? formatTimeSlotCode(data.sessions.map((s: any, index: number) => ({
        id: s.id,
        timeSlotCode: s.timeSlotCode || '',
        timeSlotName: s.timeSlotName || '',
        startTime: parseTimeObj(s.startTime),
        endTime: parseTimeObj(s.endTime),
        price: s.price ?? 0,
        priceText: s.price !== undefined && s.price !== null ? String(s.price) : '',
        enabled: s.enabled ?? 1,
        sortOrder: s.sortOrder ?? index
      })))
      : [emptySession()]
    state.ruleForm.details = data.details?.length
      ? data.details.map((d: any) => ({
        id: d.id,
        title: d.title || '',
        content: d.content || '',
        sortOrder: d.sortOrder
      }))
      : [emptyDetail()]
    if (!state.ruleForm.details[0].title && data.noticeTitle) {
      state.ruleForm.details[0].title = data.noticeTitle
    }
    if (!state.ruleForm.details[0].content && data.noticeContent) {
      state.ruleForm.details[0].content = data.noticeContent
    }
    if (state.ruleForm.coverImage) {
      fileList.value = [{
        uid: '-1',
        name: state.ruleForm.coverImage.split('/').pop() || 'cover',
        status: 'done',
        url: state.ruleForm.coverImage,
        thumbUrl: state.ruleForm.coverImage
      }]
    }
  } finally {
    state.loading.init = false
  }
}

const loadTimeSlots = async () => {
  try {
    const data: any = await dictionaryStore.getDict({ code: 'ticket_session' })
    timeSlotOptions.value = (data || []).map((item: any) => ({
      code: item.code,
      label: item.name
    }))
  } catch (e) {
    timeSlotOptions.value = []
  }
}

onMounted(() => {
  loadTimeSlots()
  if (isEdit.value) {
    loadDetail()
  }
})

watch(() => state.ruleForm.dateRange, (val: string[]) => {
  if (val?.length === 2) {
    state.ruleForm.startDate = val[0]
    state.ruleForm.endDate = val[1]
  }
}, { deep: true })
</script>

<style scoped lang="less">
@import '@/style/index.less';

// 只覆盖默认态，hover/focus 保留 Ant Design 蓝色
:deep(.ant-input-affix-wrapper:not(:hover):not(.ant-input-affix-wrapper-focused)) {
  border-color: #C5C5D3;
}

// 只覆盖默认态，hover/focus 保留 Ant Design 蓝色
:deep(.ant-select:not(.ant-select-focused) .ant-select-selector:not(:hover)) {
  border-color: #C5C5D3;
}

// 只覆盖默认态，hover/focus 保留 Ant Design 蓝色
:deep(textarea.ant-input:not(:hover):not(:focus)) {
  border-color: #C5C5D3;
}

:deep(.ant-picker:not(:hover):not(.ant-picker-focused)) {
  border-color: #C5C5D3;
}

:deep(.ant-input-number:not(:hover):not(.ant-input-number-focused)) {
  border-color: #C5C5D3;
}


.detail-page {
  .form-content {
    padding: 0;
    background: transparent;
  }

  :deep(.ant-card) {
    border-radius: 8px;
    background: transparent;
    border: none;

    .ant-card-head {
      background: #fff;
    }
  }

  :deep(.ant-card-head) {
    font-size: 18px;
    font-weight: bold;
    color: #000;
  }

  :deep(.ant-card-body) {
    padding: 0;
  }

  // 统一表单样式
  .ticket-add-form {
    --form-label-width: 80px;

    .half-width-item {
      :deep(.ant-form-item-control) {
        @media (max-width: 1700px) {
          flex: 0 0 calc(50% - var(--form-label-width)) !important;
          min-width: 300px;
          max-width: calc(50% - var(--form-label-width));
        }
      }
    }

    :deep(.ant-form-item) {
      margin-bottom: 0;
      padding-bottom: 20px;
    }

    :deep(.ant-form-item-label) {
      margin-left: 0;
      margin-right: 5px;
      width: var(--form-label-width);
      text-align: right;
      justify-content: flex-end;
      display: flex;
      align-items: center;
      height: 40px;
    }

    :deep(.ant-form-item-label > label) {
      font-size: 14px;
      line-height: 1;
      font-weight: 400;
    }

    :deep(.ant-input-affix-wrapper) {
      height: 40px;
      padding: 0 11px;
      border-radius: 4px;
      display: flex;
      align-items: center;
    }

    :deep(.ant-input-disabled) {
      height: 40px;
      border-color: #C5C5D3;
      border-radius: 4px;
    }

    :deep(.ant-select-selector) {
      height: 40px !important;
      display: flex;
      align-items: center;
      border-radius: 4px;
    }

    :deep(.ant-picker) {
      min-height: 40px;
      border-radius: 4px;
      display: flex;
      align-items: center;
    }

    :deep(.ant-input-number) {
      height: 40px;
      border-radius: 4px;
    }

    :deep(.ant-input-number-input-wrap) {
      height: 40px;
      display: flex;
      align-items: center;
    }

    :deep(.ant-input-number-input) {
      height: 38px;
    }


    // textarea 样式
    :deep(.ant-input-affix-wrapper:has(textarea)) {
      height: auto !important;
      min-height: 40px;
    }

    :deep(textarea.ant-input) {
      min-height: 100px;
      resize: vertical;
      border-radius: 4px;
      padding: 8px 22px 8px 11px;
    }
  }

  // 封面上传
  .cover-form-item {
    // 禁用 picture-card 的移除离场动画，避免旧图占据第一格时上传入口短暂出现在第二格
    :deep(.ant-upload-animate-inline-leave),
    :deep(.ant-upload-animate-inline-leave-active),
    :deep(.ant-upload-animate-inline-leave-to) {
      display: none !important;
      animation: none !important;
    }

    :deep(.ant-upload-list-item-container) {
      transition: none !important;
    }

    :deep(.ant-form-item-row) {
      align-items: flex-start;
    }

    :deep(.ant-form-item-label) {
      padding-top: 0;
      height: auto;
      align-items: flex-start;
    }

    .ant-upload-list-item-container {
      :deep(.ant-upload-list-item) {
        width: 100%;
        height: 100%;
        padding: 0 !important;
        border: 1px dashed #C5C5D3;

        &.ant-upload-list-item-done {
          position: relative;

          &::before {
            position: absolute;
            inset: 0;
            width: 100% !important;
            height: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
        }

        .ant-upload-list-item-thumbnail {
          width: 100%;
          height: 100%;

          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
        }

        .ant-upload-list-item-actions {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.45);
          opacity: 0;
          transition: opacity 0.2s;

          .ant-upload-list-item-action {
            color: #fff;
            background: transparent;
          }
        }

        &:hover .ant-upload-list-item-actions {
          opacity: 1;
        }
      }

      .ant-upload-list-item-thumbnail-file {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        color: rgba(0, 0, 0, 0.45);
        background: #fafafa;
      }

      .upload-item-name {
        display: block;
        font-size: 11px;
        word-break: break-all;
        padding: 4px 8px;
        line-height: 1.3;
        max-width: 88px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    :deep(.ant-upload-list-picture-card .ant-upload-list-item-container),
    :deep(.ant-upload-list-picture-card .ant-upload-list-picture-card-container),
    :deep(.ant-upload-select-picture-card) {
      width: 130px !important;
      height: 130px !important;
      min-width: 130px !important;
      min-height: 130px !important;
      margin: 0;
    }

    :deep(.ant-upload-list-picture-card) {
      display: flex;
      align-items: center;
    }

    :deep(.ant-upload-list-item) {
      border: 1px dashed #C5C5D3;
      padding: 0 !important;
    }

    :deep(.ant-upload-list-item-thumbnail) {
      width: 100%;
      height: 100%;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    :deep(.ant-upload-list-item-actions) {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.45);
      opacity: 0;
      transition: opacity 0.2s;

      .ant-upload-list-item-action {
        color: #fff;
        background: transparent;
      }
    }

    :deep(.ant-upload-list-item):hover .ant-upload-list-item-actions {
      opacity: 1;
    }

    .cover-upload-wrap {
      position: relative;
      width: 130px;
      height: 130px;
      display: inline-flex;
      vertical-align: top;

      :deep(.ant-upload.ant-upload-select-picture-card),
      :deep(.ant-upload-select),
      :deep(.ant-upload-select-picture-card) {
        width: 130px !important;
        height: 130px !important;
        min-width: 130px !important;
        min-height: 130px !important;
        margin: 0 !important;
      }
    }

    .upload-trigger-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
    }

    .manual-progress {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;
      padding: 0 8px;
      box-sizing: border-box;

      .manual-progress-track {
        width: 100%;
        height: 4px;
        background: rgba(0, 0, 0, 0.08);
        border-radius: 2px;
        overflow: hidden;
      }

      .manual-progress-bar {
        height: 100%;
        background: #0f813d;
        border-radius: 2px;
        transition: width 0.1s linear;
      }

      .manual-progress-text {
        font-size: 11px;
        color: #666;
        line-height: 1;
        white-space: nowrap;
      }
    }
  }

  .cover-error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: #0B0B0B;
    background: #f5f5f5;
    font-size: 28px;

    :deep(svg),
    :deep(.anticon) {
      color: #0B0B0B !important;
      fill: #0B0B0B !important;
    }
  }

  // 顶部信息区
  .top-info-content {
    display: grid;
    grid-template-columns: minmax(300px, 1fr) 2fr;
    align-items: stretch;
    gap: 60px;
    background: #fff;
    border-radius: 8px;
    padding: 35px 36px 46px 36px;
    margin: 0;

    @media (max-width: 1700px) {
      grid-template-columns: 1fr;
      gap: 0;
    }
  }

  // 场次
  .session-form-item {
    :deep(.ant-form-item-row) {
      align-items: flex-start;
    }

    :deep(.ant-form-item-label) {
      padding-top: 0;
      height: 40px;
      align-items: center;
      width: 56px;
      margin-right: 0;
    }
  }

  .session-list {
    width: 100%;
  }

  .session-row {
    display: flex;
    align-items: center;
    gap: 0;
    padding: 0;
    margin-bottom: 12px;
    background: transparent;
    border-radius: 0;

    &.session-disabled {
      .price-input-wrap {
        background: #F6F6F6;
        border-color: #C5C5D3;

        &:hover,
        &:focus-within {
          border-color: #C5C5D3;
          box-shadow: none;
        }
      }
    }

  }

  .session-select {
    width: 156px;

    :deep(.ant-select-selector) {
      height: 40px !important;
      display: flex;
      align-items: center;
      border-radius: 4px;
    }

    :deep(.ant-select-selection-item) {
      line-height: 40px;
    }

    :deep(.ant-select-selection-placeholder) {
      line-height: 40px;
    }
  }

  .session-time {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: auto;
    margin-left: 54px;

    .label {
      color: #222;
      white-space: nowrap;
      font-size: 14px;
    }
  }

  .session-time-picker {
    display: flex;
    align-items: center;
    gap: 6px;

    :deep(.ant-picker) {
      width: 88px;
      padding: 0 8px;

      input {
        text-align: center;
      }
    }

    .time-separator {
      color: #222;
      font-size: 14px;
    }
  }

  .session-price {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-left: 54px;

    .label {
      color: #222;
      white-space: nowrap;
      font-size: 14px;
    }

    .price-input-wrap {
      display: flex;
      align-items: center;
      width: 184px;
      height: 40px;
      padding: 0 8px;
      border: 1px solid #C5C5D3;
      border-radius: 4px;
      background: #fff;
      transition: border-color 0.2s;

      &:hover {
        border-color: @primary-color;
      }

      &:focus-within {
        border-color: @primary-color;
        box-shadow: 0 0 0 2px fade(@primary-color, 20%);
      }

      .price-symbol {
        color: #C5C5D3;
        font-size: 14px;
        margin-right: 4px;
        line-height: 40px;
      }

      .session-price-input {
        flex: 1;

        :deep(.ant-input),
        :deep(.ant-input-affix-wrapper) {
          border: none !important;
          box-shadow: none !important;
          background: transparent;
          height: 38px;
          padding: 0;
          text-align: left;
        }
      }
    }
  }

  .session-delete-btn {
    width: 30px;
    height: 30px;
    min-width: 30px;
    min-height: 30px;
    padding: 0;
    border: none;
    background: #0b0b0b;
    color: #fff;
    border-radius: 50% !important;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    margin-left: 23px;

    :deep(.ant-btn-icon) {
      margin: 0;
    }

    :deep(.ant-btn-icon > *) {
      transform: translate(0, 0);
    }

    &>span {
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    &:hover {
      background: #0b0b0b;
      color: #fff;
    }

    &:disabled {
      background: #d9d9d9;
      color: #fff;
    }
  }

  .add-session-btn {
    color: #0f813d !important;
    padding: 0;
    height: auto;

    &:hover:not([disabled]) {
      color: #0f813d !important;
      filter: brightness(0.8);
    }

    &[disabled] {
      color: #D1D1BB !important;
    }
  }

  // 展览日期：picker 保持默认 34px，外层 40px 容器底部预留 active-bar 溢出空间
  .date-range-item {
    :deep(.ant-form-item-row) {
      align-items: center;
    }

    :deep(.ant-form-item-control-input) {
      display: flex;
      align-items: center;
      min-height: 40px;
      padding-bottom: 2px;
      box-sizing: border-box;
    }

    :deep(.ant-picker.ant-picker-range) {
      width: 100%;
    }

    :deep(.ant-picker-range) {
      .ant-picker-input {
        input::placeholder {
          text-align: center;
        }
      }
    }
  }

  // 详情区
  .editor-form-item {
    :deep(.ant-form-item-label) {
      width: 80px;
    }

    &.no-label {
      :deep(.ant-form-item-row) {
        display: block;
      }

      :deep(.ant-form-item-label) {
        display: none;
      }

      :deep(.ant-form-item-control) {
        width: 100%;
        max-width: 100%;
        margin-left: 0;
      }

      :deep(.ant-form-item-control-input) {
        width: 100%;
      }

      :deep(.ant-form-item-control-input-content) {
        width: 100%;
        display: block;
      }
    }
  }

  .detail-title-item {
    :deep(.ant-form-item-label) {
      margin-left: 0;
      margin-right: 5px;
      width: var(--form-label-width);
      text-align: right;
      justify-content: flex-end;
      display: flex;
      align-items: center;
      height: 40px;
    }
  }

  .detail-section {
    margin: 20px 0 0;
    padding: 0;
    background: transparent;
    border-radius: 8px;
  }

  .detail-tabs {
    :deep(.ant-tabs-nav) {
      margin-bottom: 0;
      padding: 0;
      background: transparent;

      &::before {
        display: none;
      }
    }

    :deep(.ant-tabs-nav-wrap) {
      margin-bottom: 0;
    }

    :deep(.ant-tabs-tab) {
      background: #f7f7f7;
      border: none;
      border-radius: 8px 8px 0 0;
      padding: 8px 16px;
      margin-right: 8px !important;

      &+.ant-tabs-tab {
        margin-left: 0;
      }

      .ant-tabs-tab-btn {
        color: #757070;
        font-size: 16px;
        font-weight: 400;
      }
    }

    :deep(.ant-tabs-tab-active) {
      background: #fff;

      .ant-tabs-tab-btn {
        color: #0b0b0b;
        font-size: 16px;
        font-weight: 400;
        text-shadow: none;
      }
    }

    :deep(.ant-tabs-ink-bar) {
      display: none;
    }

    :deep(.ant-tabs-tab-remove) {
      margin-left: 8px;
      color: #222;

      &:hover {
        color: #ff4d4f;
      }
    }

    :deep(.ant-tabs-extra-content) {
      padding-bottom: 0;

      .ant-btn {
        color: #0f813d;
        padding: 0;
        height: 36px;
        line-height: 36px;
        background: transparent;
        border: none;

        &:hover {
          color: #0f813d;
          filter: brightness(0.8);
        }
      }
    }

    :deep(.ant-tabs-nav-add) {
      border: none !important;
      background: transparent !important;
    }

    :deep(.ant-tabs-content-holder) {
      background: #fff;
      border-radius: 0 8px 0 0;
    }

    :deep(.ant-tabs-content) {
      padding: 38px;
    }
  }

  // 底部按钮
  .detail-footer {
    display: flex;
    justify-content: center;
    padding: 0 0 20px;
    background: #fff;
    border-radius: 0 0 8px 8px;
    margin-top: 0;

    .detail-close-btn,
    .detail-confirm-btn {
      width: 120px;
      height: 40px;
      border: 1px solid #0b0b0b;
      border-radius: 20px !important;
      color: #0b0b0b;
      font-size: 16px;
      background: #fff;

      &:hover {
        color: #0b0b0b;
        border-color: #0b0b0b;
        background: #fff;
      }
    }

    .detail-confirm-btn {
      margin-left: 10px;
    }

    .detail-publish-btn {
      width: 120px;
      height: 40px;
      margin-left: 10px;
      background: #0b0b0b;
      border-color: #0b0b0b;
      border-radius: 20px !important;
      color: #fff;
      font-size: 16px;

      &:hover {
        background: #0b0b0b;
        border-color: #0b0b0b;
        color: #fff;
      }
    }
  }

  :global(.ticket-add-preview) {
    .ant-image-preview-img {
      max-width: 90vw;
      max-height: 90vh;
      object-fit: contain;
    }
  }

  :deep(.ant-picker-range) {
    .ant-picker-input {
      input::placeholder {
        text-align: center;
      }
    }
  }
}
</style>

<style lang="less">
.ticket-time-picker-popup {

  // 隐藏"此刻"按钮，防止绕过禁用时间；保留"确定"按钮
  .ant-picker-now {
    display: none !important;
  }

  // 去除 ul 底部伪元素撑出的空白
  ul.ant-picker-time-panel-column {
    &::after {
      display: none !important;
    }
  }
}
</style>
